import { uniqBy } from 'lodash';
import { useMemo } from 'react';

import { IRestaurantNode } from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import { useFavoriteStores } from '@fhs-legacy/frontend/src/hooks/favorite-stores';
import { useGetFavoriteRestaurants } from '@fhs-legacy/frontend/src/hooks/use-get-favorite-restaurants';
import { useGetNearbyRestaurants } from '@fhs-legacy/frontend/src/hooks/use-get-nearby-restaurants';
import { useGetRecentRestaurants } from '@fhs-legacy/frontend/src/hooks/use-get-recent-restaurants';
import { useAuthContext } from '@fhs-legacy/frontend/src/state/auth';
import { useGeolocation } from '@fhs-legacy/frontend/src/state/geolocation';
import { OrderLocatorTab } from '@fhs-legacy/frontend/src/utils/launchdarkly/variations';

export interface TabRestaurants {
  restaurants: IRestaurantNode[];
  loading: boolean;
  hasError: boolean;
}

interface LocatorRestaurants {
  /** Every tab's slice, so the pager can render each of its scenes at once. */
  byTab: Partial<Record<OrderLocatorTab, TabRestaurants>>;
  /** Every known restaurant, deduped — this is what the map plots markers for. */
  allRestaurants: IRestaurantNode[];
  favoriteRestaurants: IRestaurantNode[];
}

/**
 * Single source of truth for the locator's restaurant data. All three tab queries stay mounted
 * so the map can keep plotting every known store while the guest switches tabs.
 *
 * Favorites are assembled from the guest's saved store IDs plus restaurants we already have.
 * The heart icon already follows `user.details.favoriteStores`; using that same list here means
 * adding or removing a favorite updates the Favorites tab (and map hearts) on the same render,
 * without waiting for the favorite-restaurants query to refetch.
 */
export function useLocatorRestaurants(): LocatorRestaurants {
  const nearby = useGetNearbyRestaurants();
  const favorites = useGetFavoriteRestaurants();
  const recents = useGetRecentRestaurants();
  const { validFavStores } = useFavoriteStores();
  const { isAuthenticated, user } = useAuthContext();
  const { coordinatesAvailable, hasCheckedInitialPermission, loadingUserCoordinates } =
    useGeolocation();
  const waitingForUser = isAuthenticated && !user?.cognitoId;

  const allRestaurants = useMemo(
    () =>
      uniqBy(
        [...nearby.restaurants, ...favorites.restaurants, ...recents.restaurants],
        '_id'
      ).filter(restaurant => Boolean(restaurant._id)),
    [nearby.restaurants, favorites.restaurants, recents.restaurants]
  );

  const favoriteRestaurants = useMemo(() => {
    const restaurantsById = new Map(allRestaurants.map(restaurant => [restaurant._id, restaurant]));

    return validFavStores
      .map(store => restaurantsById.get(store.storeId))
      .filter((restaurant): restaurant is IRestaurantNode => Boolean(restaurant));
  }, [allRestaurants, validFavStores]);

  const byTab: Partial<Record<OrderLocatorTab, TabRestaurants>> = {
    [OrderLocatorTab.NEARBY]: {
      restaurants: nearby.restaurants,
      // Locate-me clears the GPS fix while a new one loads, which skips the nearby query.
      // Treat that wait as loading so the list shows the skeleton instead of an empty state.
      // Same while the initial permission check is still settling and there are no coords to
      // query with. Only that first check: a later one (the empty state's "Enable location
      // services" link, or the native app-active re-check) must leave the empty state mounted,
      // since it owns that request and the denied modal it opens once the check settles.
      loading:
        nearby.loading ||
        loadingUserCoordinates ||
        (!hasCheckedInitialPermission && !coordinatesAvailable),
      hasError: Boolean(nearby.error),
    },
    [OrderLocatorTab.FAVORITE]: {
      restaurants: favoriteRestaurants,
      // Keep the skeleton only until we have something to show; a heart tap must not flash
      // loading just because the favorites query is catching up. Also stay on the skeleton
      // while GetMe/session are still loading, since favorite IDs live on the user object.
      loading:
        waitingForUser ||
        (favorites.loading && favoriteRestaurants.length === 0 && validFavStores.length > 0),
      hasError: Boolean(favorites.error),
    },
    [OrderLocatorTab.RECENT]: {
      restaurants: recents.restaurants,
      // `cache-and-network` reports loading on every mount while it revalidates, so show the
      // skeleton only until there is something to list — a revisit keeps the cached stores.
      loading: recents.loading && recents.restaurants.length === 0,
      hasError: Boolean(recents.error),
    },
  };

  return {
    byTab,
    allRestaurants,
    favoriteRestaurants,
  };
}
